/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { DetailAccordionCard } from '@umati/dashboard-common';
import React, { ReactElement } from 'react';
import { Section } from 'templates/pubSub/types/TemplateType';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import SectionContent from './SectionContent';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';

interface ISectionAccordionCardProps {
  templateContext: TemplateContext;
  eventKey: string;
  section: Section;
  isExpanded?: boolean;
  startingNodeId?: NodeIdentifier;
}

export default function SectionAccordionCard(props: ISectionAccordionCardProps): ReactElement | null {
  const validationNodePath = TemplateHelperPubSub.resolveSectionValidationNodePath(props.section);

  const validationNodeFilters = TemplateHelperPubSub.createFilter(props.templateContext.namespaces, validationNodePath);

  const validationNode = usePubSubNode(validationNodeFilters, props.startingNodeId);

  const filterTitle = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.section.titleVariable?.variablePath,
  );

  const nodeTitle = usePubSubNode(filterTitle, props.startingNodeId);

  const valueTitle = usePubSubValue(nodeTitle);

  const shouldHideSection = TemplateHelperPubSub.shouldHideOptionalSection(
    validationNode,
    props.section,
    `SectionAccordionCard:${props.section.id}`,
  );

  if (shouldHideSection) {
    return null;
  }

  const colStyle = 'col-12 col-sm-6 col-md-4';

  const title = valueTitle !== undefined ? valueTitle : props.section.title;

  return (
    <DetailAccordionCard eventKey={props.eventKey} title={title} isExpanded={!!props.isExpanded}>
      <SectionContent
        templateContext={props.templateContext}
        cols={colStyle}
        section={props.section}
        startingNodeId={props.startingNodeId}
      />
    </DetailAccordionCard>
  );
}
